import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  useUsersTwoFactorDisableCreate,
  useUsersTwoFactorEnableCreate,
  useUsersTwoFactorRecoveryCodesRegenerateCreate,
  useUsersTwoFactorSetupCreate,
} from "../../services/auth/generated/users-2fa/users-2fa";
import "./TwoFactorModal.css";

interface TwoFactorModalProps {
  isOpen: boolean;
  /** Whether 2FA is currently enabled on the account. The modal opens
   * directly into the right view based on this — saves a click for
   * users who already enrolled. */
  enabled: boolean;
  /** Called after a state change (enable / disable / regenerate) so the
   * caller can invalidate the profile query. */
  onMutated: () => void;
  onClose: () => void;
}

/** All possible views inside the modal. Modeling them as an explicit
 * state machine keeps the recovery-codes screen from being shown
 * accidentally on subsequent opens — recovery codes only render when
 * we just generated them. */
type View = "status" | "setup" | "verify-enable" | "show-codes" | "disable" | "regenerate";

type HttpErrorLike = {
  status?: number;
  data?: { error?: string; detail?: string };
  response?: { status?: number; data?: { error?: string; detail?: string } };
};

const errorMessage = (err: unknown, fallback: string): string => {
  const e = (err ?? {}) as HttpErrorLike;
  const data = e.response?.data ?? e.data;
  return data?.error ?? data?.detail ?? fallback;
};

const TwoFactorModal = ({ isOpen, enabled, onMutated, onClose }: TwoFactorModalProps) => {
  const { t } = useTranslation(["company", "common"]);
  // Initial view follows account state. Once the modal is open we stay
  // on whatever view the user navigated to until close.
  const [view, setView] = useState<View>(enabled ? "status" : "setup");

  // Setup state.
  const [secret, setSecret] = useState<string | null>(null);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [enableCode, setEnableCode] = useState("");

  // Disable state.
  const [disablePassword, setDisablePassword] = useState("");
  const [disableCode, setDisableCode] = useState("");

  // Regenerate state.
  const [regenerateCode, setRegenerateCode] = useState("");

  // Codes to surface after enable / regenerate. Cleared on every other
  // view transition so they never linger on screen.
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const setupMutation = useUsersTwoFactorSetupCreate();
  const enableMutation = useUsersTwoFactorEnableCreate();
  const disableMutation = useUsersTwoFactorDisableCreate();
  const regenerateMutation = useUsersTwoFactorRecoveryCodesRegenerateCreate();

  // Reset internal state whenever the modal is reopened so a previous
  // session's secret / codes can't leak across reuses of the same
  // component instance.
  useEffect(() => {
    if (!isOpen) return;
    setView(enabled ? "status" : "setup");
    setSecret(null);
    setQrCode(null);
    setEnableCode("");
    setDisablePassword("");
    setDisableCode("");
    setRegenerateCode("");
    setRecoveryCodes([]);
    setError(null);
  }, [isOpen, enabled]);

  if (!isOpen) return null;

  const beginSetup = () => {
    setError(null);
    // Setup endpoint takes no body. The orval-generated mutation
    // accordingly types its variables as `void`.
    setupMutation.mutate(undefined, {
      onSuccess: (data) => {
        setSecret(data?.secret ?? null);
        setQrCode(data?.qrCode ?? null);
        setView("verify-enable");
      },
      onError: (err) => setError(errorMessage(err, t("twoFactor.errors.setupFailed"))),
    });
  };

  const submitEnable = (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    enableMutation.mutate(
      { data: { code: enableCode.trim() } },
      {
        onSuccess: (data) => {
          setRecoveryCodes(data?.recoveryCodes ?? []);
          setView("show-codes");
          onMutated();
        },
        onError: (err) => setError(errorMessage(err, t("twoFactor.errors.invalidCode"))),
      },
    );
  };

  const submitDisable = (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    disableMutation.mutate(
      { data: { password: disablePassword, code: disableCode.trim() } },
      {
        onSuccess: () => {
          onMutated();
          onClose();
        },
        onError: (err) => setError(errorMessage(err, t("twoFactor.errors.invalidPasswordOrCode"))),
      },
    );
  };

  const submitRegenerate = (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    regenerateMutation.mutate(
      { data: { code: regenerateCode.trim() } },
      {
        onSuccess: (data) => {
          // The regenerate endpoint isn't typed in the OpenAPI schema
          // (its `@extend_schema(responses=...)` only declares the
          // status), so orval emits `void`. Bridge through `unknown`
          // — the runtime payload genuinely carries `recoveryCodes`.
          const payload = data as unknown as { recoveryCodes?: string[] };
          setRecoveryCodes(payload?.recoveryCodes ?? []);
          setView("show-codes");
          onMutated();
        },
        onError: (err) => setError(errorMessage(err, t("twoFactor.errors.invalidCode"))),
      },
    );
  };

  const downloadCodes = () => {
    const blob = new Blob([recoveryCodes.join("\n")], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "matchpoint-recovery-codes.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const copyCodes = async () => {
    try {
      await navigator.clipboard.writeText(recoveryCodes.join("\n"));
    } catch {
      /* clipboard may be unavailable on insecure origins; the download
       * fallback above still works. */
    }
  };

  return (
    <div className="two-factor-modal-overlay" onClick={onClose}>
      <div
        className="two-factor-modal-content"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="two-factor-modal-title"
      >
        <header className="two-factor-modal-header">
          <h2 id="two-factor-modal-title" className="two-factor-modal-title">
            {t("twoFactor.title")}
          </h2>
          <button
            type="button"
            className="two-factor-modal-close"
            aria-label={t("common:actions.close")}
            onClick={onClose}
          >
            <i className="fa-solid fa-xmark" />
          </button>
        </header>

        {view === "status" && (
          <div className="two-factor-modal-body">
            <p className="two-factor-modal-status">
              <i className="fa-solid fa-circle-check two-factor-modal-status-icon" /> {t("twoFactor.status.enabled")}
            </p>
            <p className="two-factor-modal-hint">{t("twoFactor.status.hint")}</p>
            <div className="two-factor-modal-actions">
              <button type="button" className="two-factor-modal-secondary" onClick={() => setView("regenerate")}>
                {t("twoFactor.status.regenerate")}
              </button>
              <button type="button" className="two-factor-modal-danger" onClick={() => setView("disable")}>
                {t("twoFactor.disable.submit")}
              </button>
            </div>
          </div>
        )}

        {view === "setup" && (
          <div className="two-factor-modal-body">
            <p className="two-factor-modal-hint">{t("twoFactor.setup.hint")}</p>
            {error && <div className="two-factor-modal-error">{error}</div>}
            <div className="two-factor-modal-actions">
              <button type="button" className="two-factor-modal-secondary" onClick={onClose}>
                {t("common:actions.cancel")}
              </button>
              <button
                type="button"
                className="two-factor-modal-primary"
                onClick={beginSetup}
                disabled={setupMutation.isPending}
              >
                {setupMutation.isPending ? t("twoFactor.setup.preparing") : t("twoFactor.setup.start")}
              </button>
            </div>
          </div>
        )}

        {view === "verify-enable" && (
          <form className="two-factor-modal-body" onSubmit={submitEnable}>
            {qrCode && (
              <div className="two-factor-modal-qr">
                <img src={qrCode} alt={t("twoFactor.verify.qrAlt")} />
              </div>
            )}
            {secret && (
              <p className="two-factor-modal-secret">
                <Trans t={t} i18nKey="twoFactor.verify.manualKey" values={{ secret }} components={{ code: <code /> }} />
              </p>
            )}
            <div className="two-factor-modal-field">
              <label htmlFor="2fa-enable-code">{t("twoFactor.verify.codeLabel")}</label>
              <input
                id="2fa-enable-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={enableCode}
                onChange={(e) => setEnableCode(e.target.value)}
                required
              />
            </div>
            {error && <div className="two-factor-modal-error">{error}</div>}
            <div className="two-factor-modal-actions">
              <button type="button" className="two-factor-modal-secondary" onClick={onClose}>
                {t("common:actions.cancel")}
              </button>
              <button type="submit" className="two-factor-modal-primary" disabled={enableMutation.isPending}>
                {enableMutation.isPending ? t("twoFactor.verify.enabling") : t("twoFactor.verify.submit")}
              </button>
            </div>
          </form>
        )}

        {view === "show-codes" && (
          <div className="two-factor-modal-body">
            <p className="two-factor-modal-hint two-factor-modal-hint--warning">
              <i className="fa-solid fa-triangle-exclamation" />
              <Trans t={t} i18nKey="twoFactor.codes.warning" components={{ strong: <strong /> }} />
            </p>
            <ul className="two-factor-modal-codes">
              {recoveryCodes.map((c) => (
                <li key={c}>
                  <code>{c}</code>
                </li>
              ))}
            </ul>
            <div className="two-factor-modal-actions">
              <button type="button" className="two-factor-modal-secondary" onClick={copyCodes}>
                {t("twoFactor.codes.copy")}
              </button>
              <button type="button" className="two-factor-modal-secondary" onClick={downloadCodes}>
                {t("twoFactor.codes.download")}
              </button>
              <button type="button" className="two-factor-modal-primary" onClick={onClose}>
                {t("twoFactor.codes.saved")}
              </button>
            </div>
          </div>
        )}

        {view === "disable" && (
          <form className="two-factor-modal-body" onSubmit={submitDisable}>
            <p className="two-factor-modal-hint">{t("twoFactor.disable.hint")}</p>
            <div className="two-factor-modal-field">
              <label htmlFor="2fa-disable-password">{t("twoFactor.disable.password")}</label>
              <input
                id="2fa-disable-password"
                type="password"
                autoComplete="current-password"
                value={disablePassword}
                onChange={(e) => setDisablePassword(e.target.value)}
                required
              />
            </div>
            <div className="two-factor-modal-field">
              <label htmlFor="2fa-disable-code">{t("twoFactor.codeLabel")}</label>
              <input
                id="2fa-disable-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value)}
                required
              />
            </div>
            {error && <div className="two-factor-modal-error">{error}</div>}
            <div className="two-factor-modal-actions">
              <button
                type="button"
                className="two-factor-modal-secondary"
                onClick={() => setView("status")}
                disabled={disableMutation.isPending}
              >
                {t("common:actions.cancel")}
              </button>
              <button type="submit" className="two-factor-modal-danger" disabled={disableMutation.isPending}>
                {disableMutation.isPending ? t("twoFactor.disable.disabling") : t("twoFactor.disable.submit")}
              </button>
            </div>
          </form>
        )}

        {view === "regenerate" && (
          <form className="two-factor-modal-body" onSubmit={submitRegenerate}>
            <p className="two-factor-modal-hint">{t("twoFactor.regenerate.hint")}</p>
            <div className="two-factor-modal-field">
              <label htmlFor="2fa-regen-code">{t("twoFactor.codeLabel")}</label>
              <input
                id="2fa-regen-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={regenerateCode}
                onChange={(e) => setRegenerateCode(e.target.value)}
                required
              />
            </div>
            {error && <div className="two-factor-modal-error">{error}</div>}
            <div className="two-factor-modal-actions">
              <button
                type="button"
                className="two-factor-modal-secondary"
                onClick={() => setView("status")}
                disabled={regenerateMutation.isPending}
              >
                {t("common:actions.cancel")}
              </button>
              <button type="submit" className="two-factor-modal-primary" disabled={regenerateMutation.isPending}>
                {regenerateMutation.isPending ? t("twoFactor.regenerate.generating") : t("twoFactor.regenerate.submit")}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default TwoFactorModal;
